/**
 * Zod schema for `GET /api/dashboard/stats`
 * (`apps/workers/src/api/dashboardStats.ts`), consumed by
 * `@/lib/api/apiClient`'s `useDashboardStats()` hook.
 *
 * GitHub issue "bug: regular users see wrong dashboard numbers —
 * admin-only stats endpoint": this is the user-scoped stats response the
 * customer-facing `/dashboard` page (`@/pages/Dashboard.tsx`) now renders
 * instead of hard-coded `0`s. Mirrors `DashboardStatsResponse`
 * (`@herohire/types`) — duplicated as a runtime-checkable Zod schema for
 * the same reason `./admin.ts`'s schemas are (see that file's module
 * doc-comment): a shape drift throws at the query boundary instead of
 * silently rendering `undefined`.
 */

import { z } from 'zod';

export const dashboardStatsResponseSchema = z.object({
  sourcedCount: z.number().int().nonnegative(),
  activePipelineCount: z.number().int().nonnegative(),
  openConversationsCount: z.number().int().nonnegative(),
  scopedUserId: z.string(),
  /**
   * The caller's most-recently-created job (id/title), or `null` if they
   * have none yet. Added for GitHub issue "P0: Customer churn — Tom ...
   * dashboard stale, pipeline broken" so `/dashboard` can drive its
   * per-job sourcing-progress / activity-feed panels without a separate
   * "list my jobs" endpoint — `.optional().default(null)` so any older
   * cached/mocked response missing these fields still parses.
   */
  primaryJobId: z.string().nullable().optional().default(null),
  primaryJobTitle: z.string().nullable().optional().default(null),
});
export type DashboardStatsResponse = z.infer<typeof dashboardStatsResponseSchema>;
